"use client";

import * as Accordion from "@radix-ui/react-accordion";
import { typescriptHappyForwardRef } from "foxact/typescript-happy-forward-ref";
import { motion } from "motion/react";
import { useState, type ForwardedRef, type ReactNode } from "react";

interface ItemProps {
  children: ReactNode;
  cta: ReactNode;
  label: ReactNode;
  value: string;
}

export function DialogAccordion<T extends string = string>({
  section,
  sections,
  onSectionChange,
}: {
  onSectionChange?: (value: T) => void;
  section?: string;
  sections: (ItemProps | undefined)[];
}) {
  const [uncontrolledSection, setSection] = useState<string | null>(
    sections[0]?.value ?? null,
  );

  return (
    <Accordion.Root
      className="flex flex-col items-stretch gap-4"
      onValueChange={(value) => {
        setSection(value);
        onSectionChange?.(value as T);
      }}
      type="single"
      value={section ?? uncontrolledSection ?? undefined}
    >
      {sections
        .filter((x): x is ItemProps => x !== undefined)
        .map(({ value, children, cta, label }) => (
          <AccordionItem cta={cta} key={value} label={label} value={value}>
            {children}
          </AccordionItem>
        ))}
    </Accordion.Root>
  );
}

const AccordionItem = typescriptHappyForwardRef(
  (
    { children, value, cta, label }: ItemProps,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    return (
      <Accordion.Item asChild ref={ref} value={value}>
        <motion.div
          className="shadow-card-sm group-data-[state=open]:shadow-card-md group rounded-2xl bg-white"
          layout="position"
        >
          <Accordion.Trigger className="flex w-full items-center justify-between p-3 px-5 group-data-[state=open]:hidden">
            <div className="text-neutral-500">{label}</div>
            <div className="text-right font-medium">{cta}</div>
          </Accordion.Trigger>
          <Accordion.Content className="opacity-0 transition-opacity group-data-[state=open]:opacity-100">
            {children}
          </Accordion.Content>
        </motion.div>
      </Accordion.Item>
    );
  },
);
